import {UntypedFormGroup} from '@angular/forms';
import {FuelingPoiEntry} from '../../../../../../../core/component/fueling-poi/model/fueling-poi.entry';
import {ColoredRange} from './fuel-chart.component';

const REFILL_AMOUNT_OPTION_ID = -5;
const COLOR_CRITICAL_FUEL_LEVEL = 'rgb(221,117,117)';
const COLOR_LOW_FUEL_LEVEL = 'rgb(244,209,133)';
const COLOR_NORMAL_FUEL_LEVEL = 'rgba(110,165,89)';

export interface TaskFuelData {
  idx: number;
  distance: number;
  fuelConsumption: number;
}

export interface CountryFuelData {
  distance: number;
  fuelConsumption: number;
  currentCountry: string;
}

export interface ExecutingCountry {
  country: string;
  distance: number;
}

export interface FuelInfo {
  fuelUsage: number;
  fuelType: VehicleFuelType | null;
}

export type VehicleFuelType =
  'DIESEL'
  | 'LNG'
  | 'PETROL'
  | 'HVO'
  | 'CNG'
  | 'BIODIESEL'
  | 'BEV'
  | 'HYDROGEN'
  | 'DUAL'
  | 'OTHER';

export type ProductPrice =
  'diesel'
  | 'adBlue'
  | 'lng'
  | 'hvo'
  | 'cng'
  | 'biodiesel'
  | 'bev'
  | 'hydrogen';

export const FuelMap: Record<string, ProductPrice> = {
  LNG: 'lng',
  BEV: 'bev',
  CNG: 'cng',
  HVO: 'hvo',
  HYDROGEN: 'hydrogen',
  BIODIESEL: 'biodiesel'
};

export interface FuelFilter {
  prices: {id: string, itemName: string}[];
  groups: {id: number, itemName: string}[];
  highestPrice: number;
  contactless: boolean;
}

export const FuelPriceGroups = [
  {id: 'LOW', itemName: 'LOW'},
  {id: 'MEDIUM', itemName: 'MEDIUM'},
  {id: 'HIGH', itemName: 'HIGH'}
]

export const FuelPriceOptions = [
  { id: 'diesel', itemName: 'diesel' },
  { id: 'adBlue', itemName: 'adBlue' },
  { id: 'lng', itemName: 'lng' },
  { id: 'hvo', itemName: 'hvo' },
  { id: 'cng', itemName: 'cng' },
  { id: 'biodiesel', itemName: 'biodiesel' },
  { id: 'bev', itemName: 'bev' },
  { id: 'hydrogen', itemName: 'hydrogen' }
];

export const calculateTasksFuelData = (
  forms: UntypedFormGroup[],
  currentFuel: number,
  usageFor100km: number,
  fuelCapacity: number,
  traveledDistance: number): TaskFuelData[] => {

  let tasksFuelData = [];
  let fuelLevel = currentFuel;
  let distance = 0;
  for (let form of forms) {
    let wasExecuting = forms.findIndex(it => it === form) === 0 && traveledDistance > 0;
    const taskDistance = wasExecuting
      ? (getTaskDistance(form) - traveledDistance) / 1000
      : getTaskDistance(form) / 1000;
    distance += taskDistance;
    fuelLevel -= calculateFuelConsumed(taskDistance, usageFor100km);
    if (form.controls.type.value === 'FUELLING') {
      tasksFuelData.push({
        idx: form.controls.idx.value,
        distance: distance - 0.001,
        fuelConsumption: fuelLevel
      });
      fuelLevel = refillFuel(form, fuelLevel, fuelCapacity);
    }
    tasksFuelData.push({
      idx: form.controls.idx.value,
      distance: distance,
      fuelConsumption: fuelLevel
    });
  }
  return tasksFuelData;
};

export const calculateCountriesFuelData = (
  forms: UntypedFormGroup[],
  currentFuel: number,
  fuelUsageFor100km: number,
  executingCountries: any[],
  fuelCapacity: number,
  wasExecuting: boolean): CountryFuelData[] => {

  let totalDistance = 0;
  let countryFuelData = [];
  let fuelLevel = currentFuel;
  for (let form of forms) {
    let countries = executing(form.controls.status.value)
    || (forms.findIndex(it => it === form ) === 0 && wasExecuting)
      ? executingCountries
      : form.controls.countries.value;
    if (countries) {
      for (let country of countries) {
        totalDistance += country.distance / 1000;
        countryFuelData.push({
          distance: totalDistance,
          fuelConsumption: fuelLevel -= calculateFuelConsumed(country.distance / 1000, fuelUsageFor100km),
          currentCountry: country.country
        });
      }
    }
    if (form.controls.type.value === 'FUELLING') {
      fuelLevel = refillFuel(form, fuelLevel, fuelCapacity);
    }
  }
  return countryFuelData;
};

export const getRangeForEveryNKM = (
  fuelLevel: number,
  forms: UntypedFormGroup[],
  fuelUsageFor100km: number,
  fuelCapacity: number,
  totalDistance: number,
  coloredRanges: ColoredRange[]) => {

  let ranges = [{
    type: 'TASK',
    x: 0,
    low: 0,
    high: fuelLevel,
    iconUrl: ''
  }];
  totalDistance = +(totalDistance / 1000).toFixed(0);
  let everyNKm = resolveEveryN(totalDistance);
  let refuellingPoints = getRefuellingPoints(forms, totalDistance, everyNKm);
  let fuelCounter = 0;
  let calculatedFuelLevel = fuelLevel;
  let minCapacity = fuelCapacity * 0.05;
  for (let i = everyNKm; i <= totalDistance; i += everyNKm) {
    let refuellingPoint = refuellingPoints.get(i);
    fuelCounter += everyNKm;
    if (refuellingPoint != null) {
      fuelLevel = refillFuel(refuellingPoint, calculatedFuelLevel, fuelCapacity);
      fuelCounter = everyNKm;
    }
    calculatedFuelLevel = fuelLevel - calculateFuelConsumed(fuelCounter, fuelUsageFor100km);
    let rangeColor = COLOR_NORMAL_FUEL_LEVEL;
    if (calculatedFuelLevel < fuelCapacity * 0.20) {
      rangeColor = calculatedFuelLevel > minCapacity ? COLOR_LOW_FUEL_LEVEL : COLOR_CRITICAL_FUEL_LEVEL;
    }
    coloredRanges.push({
      value: i,
      color: rangeColor
    });
    ranges.push({
      type: 'TASK',
      x: i,
      low: 0,
      high: calculatedFuelLevel < 0 ? 0 : calculatedFuelLevel,
      iconUrl: 'assets/img/proTasks/markers/DONE/DEFAULT.svg'
    });
  }
  return ranges;
}

export const calculateRange = (fuel: number, fuelUsageFor100km: number): number => {
  return Math.floor(fuel / fuelUsageFor100km * 100);
}

const resolveEveryN = (distance: number): number => {
  let n = 1;
  while (distance / n > 1000) {
    n++;
  }
  return n;
}

const getRefuellingPoints = (forms: UntypedFormGroup[], totalDistance: number, everyNKm: number) => {
  let fuellingForms = forms.filter(form => form.controls.type.value === 'FUELLING');
  let totalPoints = Math.round(totalDistance / everyNKm);
  let refuellingPoints: Map<number, UntypedFormGroup> = new Map<number, UntypedFormGroup>();
  for (let form of fuellingForms) {
    let idx = forms.indexOf(form);
    let distance = 0;
    forms.slice(0, idx + 1).forEach(it => {
      distance += executing(it.controls.status.value)
      && it.controls.distanceRemaining.value < it.controls.distance.value
        ? it.controls.distanceRemaining.value
        : it.controls.distance.value;
    });
    distance = +(distance / 1000).toFixed(0);
    let point = Math.round(totalPoints * distance / totalDistance);
    let fuelingKM = point < totalPoints
      ? point * everyNKm
      : totalPoints * everyNKm;
    refuellingPoints.set(fuelingKM, form)
  }
  return refuellingPoints;
}

export const getTaskDistance = (it: UntypedFormGroup): number => {
  return executing(it.controls.status.value) && it.controls.distanceRemaining.value <= it.controls.distance.value
    ? it.controls.distanceRemaining.value
    : it.controls.distance.value;
};

export const calculateFuelConsumed = (distance: number, fuelUsageFor100km: number): number => {
  let fuelLevel = distance * fuelUsageFor100km / 100;
  return parseFloat(fuelLevel.toFixed(3));
};

const refillFuel = (form: UntypedFormGroup, fuelLevel: number, fuelCapacity: number): number => {
  let refill = form.controls.additionalInfo.value.find(info => info.optionId === REFILL_AMOUNT_OPTION_ID);
  const refilledAmount = refill != null ? Number(refill.value) : 0;
  const refilledFuelLevel = fuelLevel + refilledAmount;
  return refilledFuelLevel <= fuelCapacity && refilledAmount >= 0
    ? refilledFuelLevel
    : fuelCapacity;
};

export const executing = (status: string): boolean => {
  return status === 'EXECUTING'
    || status === 'ARRIVED'
    || status === 'WAITING'
    || status === 'IN_PROGRESS'
    || status === 'FINISHING';
};


export const setupExecuting = (
  forms: UntypedFormGroup[],
  traveledDistance: number): ExecutingCountry[] => {

  let executingCountries = [];
  let executingTasks = forms.filter(it => executing(it.controls.status.value));
  if (executingTasks.length > 0 || (traveledDistance > 0 && forms.length > 0)) {
    let executing = executingTasks.length > 0 ? executingTasks[0] : forms[0];
    let countries = executing.controls.countries.value;
    let distance = executingTasks.length > 0 ? executing.controls.distanceRemaining.value : 0;
    let drivenDistance = executingTasks.length > 0
      ? executing.controls.distance.value - distance
      : traveledDistance;
    for (let country of countries) {
      if (drivenDistance > 0) {
        drivenDistance -= country.distance;
        if (drivenDistance < 0) {
          executingCountries.push({
            country: country.country,
            distance: drivenDistance * -1
          });
        }
      } else {
        executingCountries.push({
          country: country.country,
          distance: country.distance
        });
      }
    }
  }
  return executingCountries;
};

export const checkForFuelWarning = (
  taskFuelData: TaskFuelData[],
  fuelCapacity: number,
  fuelWarningThreshold: number): boolean => {

  return taskFuelData.some(it => it.fuelConsumption < fuelCapacity * fuelWarningThreshold);
};

export const getRemainingPolyline = (
  forms: UntypedFormGroup[],
  routeLatLng: any[],
  lat: number,
  lng: number): google.maps.LatLng[] => {

  let newIndex = pointIndex(routeLatLng, new google.maps.LatLng(lat, lng));
  routeLatLng.splice(0, newIndex);
  return routeLatLng;
}

export const pointIndex = (routeLatLng: google.maps.LatLng[], latLng: google.maps.LatLng) => {
  let diffArray = [];
  for (let point of routeLatLng) {
    let elementDiff = elementDifference(routeLatLng, point, latLng);
    diffArray.push(elementDiff);
  }
  diffArray.sort((a, b) => a.diff > b.diff ? 1 : -1);
  return diffArray.length > 0 ? diffArray[0].idx : -1
}

export const elementDifference = (
  routeLatLng: google.maps.LatLng[],
  point: google.maps.LatLng,
  location: google.maps.LatLng) => {

  let diff = Math.abs(location.lat() - point.lat()) + Math.abs(location.lng() - point.lng());
  let idx = routeLatLng.indexOf(point);
  return {
    diff: diff,
    idx: idx
  }
}

export const resolveFuelingPriceKey = (productPrice: ProductPrice): keyof FuelingPoiEntry => {
  if (productPrice === 'diesel') {
    return 'fuelPrice';
  } else if (productPrice === 'adBlue') {
    return 'adbluePrice';
  } else if (productPrice === 'lng') {
    return 'lngPrice';
  } else if (productPrice === 'hvo') {
    return 'hvoPrice';
  } else if (productPrice === 'cng') {
    return 'cngPrice';
  } else if (productPrice === 'biodiesel') {
    return 'biodieselPrice';
  } else if (productPrice === 'bev') {
    return 'bevPrice';
  } else if (productPrice === 'hydrogen') {
    return 'hydrogenPrice';
  }
  return null;
};

export const resolveFuelingPriceKeys = (
  productPrices: ProductPrice[]
): (keyof FuelingPoiEntry)[] => {

  const map: Record<ProductPrice, keyof FuelingPoiEntry> = {
    diesel: 'fuelPrice',
    adBlue: 'adbluePrice',
    lng: 'lngPrice',
    hvo: 'hvoPrice',
    cng: 'cngPrice',
    biodiesel: 'biodieselPrice',
    bev: 'bevPrice',
    hydrogen: 'hydrogenPrice'
  };

  return (productPrices || [])
    .map(p => map[p])
    .filter(Boolean);
};

export function formatFuelPrice(price: number): string {
  let rounded = (+price.toFixed(4)).toString();
  return rounded.includes('.') ? rounded : rounded + '.0';
}
